import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowDown,
  IconArrowUp,
  IconCornerDownRight,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { usePermission } from "@how/app-kit";
import { checkMenuItems, type MenuDraftItem, type MenuProblem } from "@how/website-core";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "@how/ui";
import { MENUS, useMenus, usePublishMenu, useSaveMenu, type MenuName } from "./navigation-queries";
import { usePages } from "./website-queries";
import { WebsiteTabs } from "./WebsiteTabs";
import { MenuPreview } from "./studio/MenuPreview";
import { StudioShell } from "./studio/StudioShell";
import { useWebsiteVariant } from "./website-variant";

const LABELS: Record<MenuName, string> = { header: "Header", footer: "Footer" };

const DESCRIPTIONS: Record<MenuName, string> = {
  header: "The links across the top of every page of your website.",
  footer: "The links at the bottom of every page. Often contact details and service times.",
};

/*
 * How a top-level header item is shown. A theme with a section row (Revival) draws the two buttons there, the main
 * one filled and the second outlined; every other theme draws them as ordinary links.
 */
const SHOW_AS = [
  { value: "link", label: "A link" },
  { value: "primary", label: "Main button" },
  { value: "secondary", label: "Second button" },
];

/** Moves an item within its list. Returns the same list when the move would leave it. */
function move<T>(items: readonly T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length) return [...items];
  const next = [...items];
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}

interface ItemEditorProps {
  readonly item: MenuDraftItem;
  readonly path: readonly number[];
  readonly problems: readonly MenuProblem[];
  readonly pageOptions: readonly { readonly value: string; readonly label: string }[];
  readonly disabled: boolean;
  readonly canAddChild: boolean;
  /** A top-level header item can be one of the site's two buttons (ADR 0073, the section row). */
  readonly canBeButton?: boolean;
  readonly onChange: (item: MenuDraftItem) => void;
  readonly onRemove: () => void;
  readonly onMove: (delta: number) => void;
  readonly onAddChild: () => void;
}

const samePath = (a: readonly number[], b: readonly number[]) =>
  a.length === b.length && a.every((n, i) => n === b[i]);

function ItemEditor({
  item,
  path,
  problems,
  pageOptions,
  disabled,
  canAddChild,
  canBeButton = false,
  onChange,
  onRemove,
  onMove,
  onAddChild,
}: ItemEditorProps) {
  const mine = problems.filter((p) => samePath(p.path, path)).map((p) => p.message);
  const linksToPage = item.pageId !== undefined && item.pageId !== "";
  const position = path.join(".");
  return (
    <Stack gap="xs">
      <Group gap="xs" align="flex-start" wrap="nowrap">
        <TextInput
          label="Label"
          aria-label={`Label of menu item ${position}`}
          placeholder="What visitors read"
          value={item.label}
          disabled={disabled}
          onChange={(event) => {
            onChange({ ...item, label: event.currentTarget.value });
          }}
          style={{ flexGrow: 1 }}
        />
        <SegmentedControl
          mt={25}
          size="xs"
          aria-label={`What menu item ${position} links to`}
          disabled={disabled}
          value={linksToPage ? "page" : "address"}
          data={[
            { value: "page", label: "A page" },
            { value: "address", label: "An address" },
          ]}
          onChange={(value) => {
            // Setting one clears the other: an item links to a page or to an address, never both.
            onChange(
              value === "page"
                ? { ...item, pageId: pageOptions[0]?.value ?? "", href: undefined }
                : { ...item, pageId: undefined, href: "" },
            );
          }}
        />
      </Group>
      <Group gap="xs" align="flex-start" wrap="nowrap">
        {linksToPage ? (
          <Select
            label="Page"
            aria-label={`Page that menu item ${position} links to`}
            placeholder={pageOptions.length === 0 ? "No pages yet" : "Choose a page"}
            data={[...pageOptions]}
            value={item.pageId ?? null}
            disabled={disabled || pageOptions.length === 0}
            searchable
            onChange={(value) => {
              onChange({ ...item, pageId: value ?? "" });
            }}
            style={{ flexGrow: 1 }}
          />
        ) : (
          <TextInput
            label="Address"
            aria-label={`Address that menu item ${position} links to`}
            placeholder="https://example.org, /give, mailto: or tel:"
            value={item.href ?? ""}
            disabled={disabled}
            onChange={(event) => {
              onChange({ ...item, href: event.currentTarget.value });
            }}
            style={{ flexGrow: 1 }}
          />
        )}
        {canBeButton ? (
          <Select
            label="Show as"
            aria-label={`How menu item ${position} is shown`}
            data={SHOW_AS}
            value={item.action ?? "link"}
            disabled={disabled}
            allowDeselect={false}
            onChange={(value) => {
              onChange({
                ...item,
                action: value === "primary" || value === "secondary" ? value : undefined,
              });
            }}
            style={{ width: 170, flexShrink: 0 }}
          />
        ) : null}
        <Group gap={4} mt={25} wrap="nowrap">
          <Tooltip label="Move up">
            <ActionIcon
              variant="subtle"
              aria-label={`Move menu item ${position} up`}
              disabled={disabled}
              onClick={() => {
                onMove(-1);
              }}
            >
              <IconArrowUp size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Move down">
            <ActionIcon
              variant="subtle"
              aria-label={`Move menu item ${position} down`}
              disabled={disabled}
              onClick={() => {
                onMove(1);
              }}
            >
              <IconArrowDown size={16} />
            </ActionIcon>
          </Tooltip>
          {canAddChild ? (
            <Tooltip label="Add a link underneath this one">
              <ActionIcon
                variant="subtle"
                aria-label={`Add a link under menu item ${position}`}
                disabled={disabled}
                onClick={onAddChild}
              >
                <IconCornerDownRight size={16} />
              </ActionIcon>
            </Tooltip>
          ) : null}
          <Tooltip label="Remove">
            <ActionIcon
              variant="subtle"
              color="red"
              aria-label={`Remove menu item ${position}`}
              disabled={disabled}
              onClick={onRemove}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      {mine.map((message) => (
        <Text key={message} size="xs" c="red">
          {message}
        </Text>
      ))}
    </Stack>
  );
}

interface MenuEditorProps {
  readonly menu: MenuName;
  readonly items: readonly MenuDraftItem[];
  readonly problems: readonly MenuProblem[];
  readonly pageOptions: readonly { readonly value: string; readonly label: string }[];
  readonly disabled: boolean;
  readonly onChange: (items: readonly MenuDraftItem[]) => void;
}

const BLANK: MenuDraftItem = { label: "", href: "" };

function MenuEditor({ menu, items, problems, pageOptions, disabled, onChange }: MenuEditorProps) {
  if (items.length === 0) {
    return (
      <EmptyState
        title="This menu is empty"
        description={
          menu === "header"
            ? "Add the handful of links a first-time visitor needs: who you are, when you meet, how to get in touch."
            : "Add the links that belong at the bottom of every page."
        }
      />
    );
  }
  return (
    <Stack gap="lg">
      {items.map((item, index) => (
        <Stack gap="sm" key={index}>
          <ItemEditor
            item={item}
            path={[index]}
            problems={problems}
            pageOptions={pageOptions}
            disabled={disabled}
            canAddChild
            canBeButton={menu === "header"}
            onChange={(next) => {
              onChange(items.map((row, i) => (i === index ? next : row)));
            }}
            onRemove={() => {
              onChange(items.filter((_, i) => i !== index));
            }}
            onMove={(delta) => {
              onChange(move(items, index, index + delta));
            }}
            onAddChild={() => {
              onChange(
                items.map((row, i) =>
                  i === index ? { ...row, children: [...(row.children ?? []), BLANK] } : row,
                ),
              );
            }}
          />
          {(item.children ?? []).length > 0 ? (
            <Stack gap="lg" pl="xl" style={{ borderLeft: "2px solid var(--how-border-subtle)" }}>
              {(item.children ?? []).map((child, childIndex) => (
                <ItemEditor
                  key={childIndex}
                  item={child}
                  path={[index, childIndex]}
                  problems={problems}
                  pageOptions={pageOptions}
                  disabled={disabled}
                  canAddChild={false}
                  onChange={(next) => {
                    onChange(
                      items.map((row, i) =>
                        i === index
                          ? {
                              ...row,
                              children: (row.children ?? []).map((c, ci) =>
                                ci === childIndex ? next : c,
                              ),
                            }
                          : row,
                      ),
                    );
                  }}
                  onRemove={() => {
                    onChange(
                      items.map((row, i) =>
                        i === index
                          ? {
                              ...row,
                              children: (row.children ?? []).filter((_, ci) => ci !== childIndex),
                            }
                          : row,
                      ),
                    );
                  }}
                  onMove={(delta) => {
                    onChange(
                      items.map((row, i) =>
                        i === index
                          ? {
                              ...row,
                              children: move(row.children ?? [], childIndex, childIndex + delta),
                            }
                          : row,
                      ),
                    );
                  }}
                  onAddChild={() => undefined}
                />
              ))}
            </Stack>
          ) : null}
        </Stack>
      ))}
    </Stack>
  );
}

/** The church's header and footer menus: edited as a draft, and seen by visitors only once published. */
export function MenusPage() {
  const studio = useWebsiteVariant() === "studio";
  const menus = useMenus();
  const pages = usePages();
  const canManage = usePermission("website.manage", { write: true });
  const canPublish = usePermission("website.publish", { write: true });
  const saveMenu = useSaveMenu();
  const publishMenu = usePublishMenu();
  const [active, setActive] = useState<MenuName>("header");
  const [drafts, setDrafts] = useState<Record<string, readonly MenuDraftItem[]> | null>(null);

  // The saved menus are the starting point; after that the page holds what the person is typing.
  useEffect(() => {
    if (menus.data !== undefined && drafts === null) {
      setDrafts(Object.fromEntries(menus.data.map((m) => [m.menu, m.draft])));
    }
  }, [menus.data, drafts]);

  if (studio && (menus.isPending || pages.isPending || drafts === null || menus.isError)) {
    return (
      <StudioShell section="menus" title="Menus">
        {menus.isError ? (
          <ErrorState
            message="Could not load the menus."
            onRetry={() => {
              void menus.refetch();
            }}
          />
        ) : (
          <LoadingState rows={4} />
        )}
      </StudioShell>
    );
  }

  if (menus.isPending || pages.isPending || drafts === null) {
    return (
      <>
        <PageHeader
          title="Website"
          description="The links visitors use to find their way around."
        />
        <WebsiteTabs active="menus" />
        <LoadingState rows={4} />
      </>
    );
  }

  if (menus.isError) {
    return (
      <>
        <PageHeader
          title="Website"
          description="The links visitors use to find their way around."
        />
        <WebsiteTabs active="menus" />
        <Alert color="red" title="Could not load the menus">
          <Button
            variant="subtle"
            onClick={() => {
              void menus.refetch();
            }}
          >
            Try again
          </Button>
        </Alert>
      </>
    );
  }

  const state = menus.data.find((m) => m.menu === active);
  const items = drafts[active] ?? [];
  const pageIds = new Set((pages.data ?? []).map((p) => p.id));
  const problems = checkMenuItems(items, { pageIds });
  const pageOptions = (pages.data ?? []).map((p) => ({ value: p.id, label: p.title }));
  const editable = canManage.data === true;

  const setItems = (next: readonly MenuDraftItem[]) => {
    setDrafts({ ...drafts, [active]: next });
  };

  const menuActions = editable ? (
    <Group gap="xs">
      <Button
        variant="default"
        loading={saveMenu.isPending}
        disabled={problems.length > 0}
        onClick={() => {
          saveMenu.mutate(
            { menu: active, items },
            {
              onSuccess: () => {
                notifications.show({
                  color: "teal",
                  title: "Saved",
                  message: `The ${LABELS[active].toLowerCase()} menu is saved as a draft.`,
                });
              },
              onError: () => {
                notifications.show({
                  color: "red",
                  title: "Not saved",
                  message: "Check your access and try again.",
                });
              },
            },
          );
        }}
      >
        Save draft
      </Button>
      {canPublish.data === true ? (
        <Button
          loading={publishMenu.isPending}
          disabled={problems.length > 0 || saveMenu.isPending}
          onClick={() => {
            // Publishing the draft on screen means saving it first; otherwise a person publishes what they
            // last saved and wonders why their edit is missing.
            saveMenu.mutate(
              { menu: active, items },
              {
                onSuccess: () => {
                  publishMenu.mutate(active, {
                    onSuccess: () => {
                      notifications.show({
                        color: "teal",
                        title: "Published",
                        message: `Visitors now see the ${LABELS[active].toLowerCase()} menu.`,
                      });
                    },
                    onError: () => {
                      notifications.show({
                        color: "red",
                        title: "Not published",
                        message: "The draft was saved, but publishing failed.",
                      });
                    },
                  });
                },
                onError: () => {
                  notifications.show({
                    color: "red",
                    title: "Not published",
                    message: "The draft could not be saved, so nothing was published.",
                  });
                },
              },
            );
          }}
        >
          Publish
        </Button>
      ) : null}
    </Group>
  ) : undefined;
  const switcher = (
    <Group justify="space-between" align="center">
      <SegmentedControl
        aria-label="Which menu to edit"
        value={active}
        onChange={(value) => {
          setActive(value as MenuName);
        }}
        data={MENUS.map((menu) => ({ value: menu, label: LABELS[menu] }))}
      />
      {state?.publishedAt === null ? (
        <Badge variant="light" color="gray">
          Never published
        </Badge>
      ) : state?.changed === true ? (
        <Badge variant="light" color="orange">
          Unpublished changes
        </Badge>
      ) : (
        <Badge variant="light" color="teal">
          Published
        </Badge>
      )}
    </Group>
  );
  const editorBody = (
    <>
      <MenuEditor
        menu={active}
        items={items}
        problems={problems}
        pageOptions={pageOptions}
        disabled={!editable}
        onChange={setItems}
      />
      {editable ? (
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setItems([...items, BLANK]);
            }}
          >
            Add a link
          </Button>
        </Group>
      ) : null}
    </>
  );
  const note = (
    <Text size="sm" c="dimmed">
      A link to one of your pages follows that page: change the page&apos;s address and the menu
      still works. Nothing here reaches visitors until it is published.
    </Text>
  );

  if (studio) {
    return (
      <StudioShell
        section="menus"
        title="Menus"
        description="The links visitors use to find their way around, beside the header or footer they make."
        actions={menuActions}
      >
        {switcher}
        <div className="ws-menus">
          <section className="ws-panel" aria-labelledby="ws-menu-heading">
            <h3 className="ws-panel__title" id="ws-menu-heading">
              {LABELS[active]} links
            </h3>
            <p className="ws-panel__text">{DESCRIPTIONS[active]}</p>
            <Stack gap="md">{editorBody}</Stack>
          </section>
          <aside className="ws-menus__preview" aria-labelledby="ws-menu-preview-heading">
            <div className="ws-panel">
              <h3 className="ws-panel__title" id="ws-menu-preview-heading">
                How it looks
              </h3>
              <p className="ws-panel__text">
                {active === "header"
                  ? "Across the top of every page, with sub-links opened under their link."
                  : "At the foot of every page, a column for each link."}
              </p>
              <MenuPreview menu={active} items={items} />
            </div>
          </aside>
        </div>
        {note}
      </StudioShell>
    );
  }

  return (
    <>
      <PageHeader
        title="Website"
        description="The links visitors use to find their way around."
        actions={menuActions}
      />
      <WebsiteTabs active="menus" />
      <Stack gap="md">
        {switcher}

        <Card>
          <Stack gap="md">
            <div>
              <Title order={4}>{LABELS[active]} menu</Title>
              <Text size="sm" c="dimmed">
                {DESCRIPTIONS[active]}
              </Text>
            </div>
            {editorBody}
          </Stack>
        </Card>

        {note}
      </Stack>
    </>
  );
}
